import {
	CardDocumentId,
	ChatDocumentId,
	DraftDocumentId,
	PreprintDocumentId,
	ThreadsDocumentId,
	WhiteboardDocumentId,
} from "../classes";
import { parseDocumentIdString } from "../parsing-utils";
import type {
	DocumentGenerationVersion,
	DocumentHistoryVersion,
	DocumentIdInstance,
	DocumentTypes,
	InferDocumentType,
} from "../types";
import { isDocumentId } from "../validation";

// Constructor map for creating instances
const constructorMap: Record<
	DocumentTypes,
	(
		id: string,
		generationVersion?: DocumentGenerationVersion,
		historyVersion?: DocumentHistoryVersion,
	) => DocumentIdInstance
> = {
	whiteboard: (id, generationVersion, historyVersion) =>
		new WhiteboardDocumentId(id, generationVersion, historyVersion),
	card: (id, generationVersion, historyVersion) =>
		new CardDocumentId(id, generationVersion, historyVersion),
	preprint: (id, generationVersion, historyVersion) =>
		new PreprintDocumentId(id, generationVersion, historyVersion),
	draft: (id, generationVersion, historyVersion) =>
		new DraftDocumentId(id, generationVersion, historyVersion),
	threads: (id, generationVersion, historyVersion) =>
		new ThreadsDocumentId(id, generationVersion, historyVersion),
	chat: (id, generationVersion, historyVersion) =>
		new ChatDocumentId(id, generationVersion, historyVersion),
};

// Class map for validation
type DocumentIdConstructor =
	| typeof WhiteboardDocumentId
	| typeof CardDocumentId
	| typeof PreprintDocumentId
	| typeof DraftDocumentId
	| typeof ThreadsDocumentId
	| typeof ChatDocumentId;

const classMap: Record<DocumentTypes, DocumentIdConstructor> = {
	whiteboard: WhiteboardDocumentId,
	card: CardDocumentId,
	preprint: PreprintDocumentId,
	draft: DraftDocumentId,
	threads: ThreadsDocumentId,
	chat: ChatDocumentId,
};

export function fromDocumentIdString<T extends string>(
	idString: T,
): InferDocumentType<T>;

export function fromDocumentIdString(
	idString: string,
	DocumentClass: typeof WhiteboardDocumentId,
): WhiteboardDocumentId;

export function fromDocumentIdString(
	idString: string,
	DocumentClass: typeof CardDocumentId,
): CardDocumentId;

export function fromDocumentIdString(
	idString: string,
	DocumentClass: typeof PreprintDocumentId,
): PreprintDocumentId;

export function fromDocumentIdString(
	idString: string,
	DocumentClass: typeof DraftDocumentId,
): DraftDocumentId;

export function fromDocumentIdString(
	idString: string,
	DocumentClass: typeof ThreadsDocumentId,
): ThreadsDocumentId;

export function fromDocumentIdString(
	idString: string,
	DocumentClass: typeof ChatDocumentId,
): ChatDocumentId;

export function fromDocumentIdString(
	idString: string,
	DocumentClass?: DocumentIdConstructor,
): DocumentIdInstance {
	const { type, id, generationVersion, historyVersion } =
		parseDocumentIdString(idString);

	if (DocumentClass && classMap[type] !== DocumentClass) {
		throw new Error(`Document type "${type}" does not match expected class`);
	}

	return constructorMap[type](id, generationVersion, historyVersion);
}

/**
 * Parses a typed document id (`Whiteboard-…`) or a raw aggregate id (nano id)
 * into a {@link WhiteboardDocumentId} instance.
 *
 * Use the returned object's `toIdString()` method for GraphQL/command ids.
 */
export function parseWhiteboardDocumentId(ref: string): WhiteboardDocumentId {
	if (isDocumentId(ref)) {
		return fromDocumentIdString(ref, WhiteboardDocumentId);
	}
	return new WhiteboardDocumentId(ref);
}

/**
 * Parses a typed document id (`Card-…` or `Preprint-…`) or a raw aggregate id (nano id)
 * into a {@link CardDocumentId} or {@link PreprintDocumentId} instance.
 *
 * Use the returned object's `toIdString()` method for GraphQL/command ids.
 */
export function parseCardDocumentId(
	ref: string,
): CardDocumentId | PreprintDocumentId {
	if (isDocumentId(ref)) {
		const doc = fromDocumentIdString(ref);
		if (doc instanceof CardDocumentId || doc instanceof PreprintDocumentId) {
			return doc;
		}
		throw new Error(
			`Document type "${doc.type}" does not match expected card class`,
		);
	}
	return new CardDocumentId(ref);
}

/**
 * Parses a typed document id (`Chat-…`) or a raw aggregate id (nano id)
 * into a {@link ChatDocumentId} instance.
 *
 * Use the returned object's `toIdString()` method for GraphQL/command ids.
 */
export function parseChatDocumentId(ref: string): ChatDocumentId {
	if (isDocumentId(ref)) {
		const doc = fromDocumentIdString(ref);
		if (doc instanceof ChatDocumentId) {
			return doc;
		}
		throw new Error(
			`Document type "${doc.type}" does not match expected chat class`,
		);
	}
	if (ref.toLowerCase().startsWith("chat-")) {
		return new ChatDocumentId(ref.slice(5));
	}
	return new ChatDocumentId(ref);
}
